import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { renewalsReportTabs } from "../components/ActionReports/RenewalsActionReport/RenewalsActionReport";

/**
 * Redux state interface for Renewals Action Report.
 *
 * @interface RenewalsARState
 * @property {renewalsReportTabs} defaultView - Default tab view on page load
 * @property {renewalsReportTabs} activeView - Currently active tab view
 * @property {renewalsReportTabs[]} activeTabs - List of available tabs
 * @property {string[]} affAbosToExpand - Array of AffAbo identifiers (format: "aff-abo") to auto-expand in tree view
 */
interface RenewalsARState {
  defaultView: renewalsReportTabs;
  activeView: renewalsReportTabs;
  activeTabs: renewalsReportTabs[];
  affAbosToExpand: string[];
}

export const initialState: RenewalsARState = {
  defaultView: renewalsReportTabs.NON_AUTO_RENEWAL,
  activeView: renewalsReportTabs.NON_AUTO_RENEWAL,
  activeTabs: [renewalsReportTabs.NON_AUTO_RENEWAL, renewalsReportTabs.ON_AUTO_RENEWAL],
  affAbosToExpand: [],
};

const { actions, reducer } = createSlice({
  name: "renewalsActionReport",
  initialState,
  reducers: {
    setActiveTabView: (state, action: PayloadAction<renewalsReportTabs>) => {
      const { payload } = action;
      return {
        ...state,
        activeView: payload,
      };
    },
    setActiveTabs: (state, action: PayloadAction<renewalsReportTabs[]>) => {
      const { payload } = action;
      const newActiveView = payload.includes(state.activeView) ? state.activeView : payload[0];
      return {
        ...state,
        activeTabs: payload,
        activeView: newActiveView,
      };
    },
    /**
     * Sets the list of AffAbo identifiers to auto-expand in tree view.
     *
     * When tree view is activated, this action stores the list of ABOs
     * currently displayed in the table report. The tree component subscribes
     * to this state and automatically expands nodes to reveal these ABOs.
     *
     * @param {string[]} action.payload - Array of AffAbo identifiers in format "aff-abo" (e.g., ["123-456", "789-012"])
     */
    setAffAbosToExpand: (state, action: PayloadAction<string[]>) => {
      const { payload } = action;
      return {
        ...state,
        affAbosToExpand: payload,
      };
    },
  },
});

export const { setActiveTabView, setActiveTabs, setAffAbosToExpand } = actions;

export default reducer;
